import { useRef, useState } from "react";
import { Modal } from "antd";
import { careService } from "../../services/careService";
import { prescriptionFileError } from "./prescriptionImport";

export default function PrescriptionImportDialog({ residentId, residentLabel, workspaceParams, actorId, uploadFile, onImported, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const uploaded = useRef(null);
  const inFlight = useRef(false);

  const importPrescription = async () => {
    if (inFlight.current) return;
    const invalid = prescriptionFileError(file);
    if (invalid) { setError(invalid); return; }
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      if (!uploaded.current) {
        const result = await uploadFile(null, file, {
          confirmUpload: false, fileType: file.type || "DOCUMENT", includeUserId: false,
          preserveOriginal: true, purpose: "OTHER", skipUserValidation: true,
        });
        const fileId = result?.fileId || result?.file?.id;
        if (!fileId) throw new Error("Source upload did not complete. Please retry.");
        uploaded.current = { fileId, storageFileId: fileId, fileName: file.name,
          originalFileName: file.name, contentType: file.type, size: file.size,
          uploaded: true, confirmed: false, storageStatus: "UPLOADED_NOT_CONFIRMED" };
      }
      const records = await careService.residentMedications.importPrescription(
        residentId, file, uploaded.current, workspaceParams, actorId,
      );
      if (!Array.isArray(records) || !records.length) throw new Error("No medication drafts were returned.");
      onImported(records);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Unable to import prescription. Please retry.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal open title="Import prescription" onCancel={onClose} closable={!busy} maskClosable={!busy}
      keyboard={!busy} onOk={importPrescription} confirmLoading={busy}
      okText={busy ? "Extracting medications…" : "Import medication drafts"}
      okButtonProps={{ disabled: !file || busy }} cancelButtonProps={{ disabled: busy }}>
      <p>Import for <strong>{residentLabel}</strong>. Check that this is the patient named on the prescription.</p>
      <p>Each medication becomes a separate draft in Med Approvals. Open each draft to verify the prefilled fields against the document and complete approval before it enters the Med List.</p>
      <label htmlFor="prescription-import-file">Prescription document (PDF, DOCX, PNG or JPEG; up to 10 MB)</label>
      <input id="prescription-import-file" type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" disabled={busy}
        style={{ display: "block", margin: "12px 0", maxWidth: "100%" }}
        onChange={(event) => {
          const next = event.target.files?.[0] || null;
          setFile(next); uploaded.current = null; setError(next ? prescriptionFileError(next) : "");
        }} />
      <p><small>The document is processed by Gemini to extract medication details. Extraction can take a few minutes.</small></p>
      {busy ? <p role="status">Uploading and extracting. Keep this window open until the drafts are ready.</p> : null}
      {error ? <p role="alert" className="form-error">{error}</p> : null}
    </Modal>
  );
}
